import { FormEvent, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import {
  ALL_WORKING_DAYS,
  TenantSettings,
  UpdateTenantSettingsInput,
  WORKING_DAY_LABELS,
  WorkingDay,
  updateTenantSettings,
  useTenantSettings,
} from "../api/tenantSettings";
import { useAuth } from "../auth/AuthContext";
import { ScheduleSettingsSummary } from "../components/settings/ScheduleSettingsSummary";
import { UI_LOADING } from "../constants/uiLabels";
import "./SettingsPage.css";

type FormState = {
  displayName: string;
  legalName: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  taxNumber: string;
  timezone: string;
  workingDays: WorkingDay[];
  brandPrimaryColor: string;
  brandSecondaryColor: string;
  logoUrl: string;
  defaultLessonDurationMinutes: number;
};

function settingsToForm(s: TenantSettings | null): FormState {
  return {
    displayName: s?.display_name ?? "",
    legalName: s?.legal_name ?? "",
    phone: s?.phone ?? "",
    email: s?.email ?? "",
    website: s?.website ?? "",
    address: s?.address ?? "",
    taxNumber: s?.tax_number ?? "",
    timezone: s?.timezone ?? "Europe/Istanbul",
    workingDays: (s?.working_days as WorkingDay[]) ?? ["mon", "tue", "wed", "thu", "fri", "sat"],
    brandPrimaryColor: s?.brand_primary_color ?? "",
    brandSecondaryColor: s?.brand_secondary_color ?? "",
    logoUrl: s?.logo_url ?? "",
    defaultLessonDurationMinutes: s?.default_lesson_duration_minutes ?? 100,
  };
}

function formToInput(form: FormState): UpdateTenantSettingsInput {
  const empty = (v: string) => (v.trim().length === 0 ? null : v.trim());
  return {
    displayName: empty(form.displayName),
    legalName: empty(form.legalName),
    phone: empty(form.phone),
    email: empty(form.email),
    website: empty(form.website),
    address: empty(form.address),
    taxNumber: empty(form.taxNumber),
    timezone: form.timezone.trim() || "Europe/Istanbul",
    workingDays: form.workingDays,
    brandPrimaryColor: empty(form.brandPrimaryColor),
    brandSecondaryColor: empty(form.brandSecondaryColor),
    logoUrl: empty(form.logoUrl),
    defaultLessonDurationMinutes: form.defaultLessonDurationMinutes,
  };
}

const TIMEZONE_OPTIONS = [
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Berlin",
  "UTC",
];

export function SettingsPage() {
  const { session } = useAuth();
  const role = session?.user?.role;
  const canEdit = role === "tenant_admin";

  const [tab, setTab] = useState<"profile" | "schedule">("profile");
  const { data, loading, error, reload } = useTenantSettings();
  const [form, setForm] = useState<FormState>(() => settingsToForm(null));
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    setForm(settingsToForm(data));
  }, [data]);

  const dirty = useMemo(() => {
    const original = settingsToForm(data);
    return JSON.stringify(original) !== JSON.stringify(form);
  }, [data, form]);

  const toggleDay = (day: WorkingDay) => {
    setForm((prev) => {
      const has = prev.workingDays.includes(day);
      const next = has
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day];
      const ordered = ALL_WORKING_DAYS.filter((d) => next.includes(d));
      return { ...prev, workingDays: ordered };
    });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    if (form.workingDays.length === 0) {
      setSaveError("En az 1 calisma Günu secmelisiniz.");
      return;
    }
    setSubmitting(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      await updateTenantSettings(formToInput(form));
      setSaveOk(true);
      reload();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Kaydedilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (role !== "tenant_admin" && role !== "manager" && role !== "office_staff") {
    return (
      <div className="kp-settings">
        <div className="kp-card kp-settings__placeholder">
          Bu sayfayi Görüntülemek icin yetkiniz yok.
        </div>
      </div>
    );
  }

  return (
    <div className="kp-settings">
      <div className="kp-settings__header">
        <h1>Kurum Ayarları</h1>
        <p>Kurum kimligi, marka, calisma Günleri ve varsayilan ders suresi.</p>
      </div>

      <div className="kp-settings__tabs">
        <button
          type="button"
          className={`kp-settings__tab${tab === "profile" ? " kp-settings__tab--active" : ""}`}
          onClick={() => setTab("profile")}
        >
          Kurum Profili
        </button>
        <button
          type="button"
          className={`kp-settings__tab${tab === "schedule" ? " kp-settings__tab--active" : ""}`}
          onClick={() => setTab("schedule")}
        >
          Rezervasyon Şablonu
        </button>
      </div>

      {tab === "profile" && (
        <form onSubmit={onSubmit} className="kp-card kp-settings__panel">
          {!canEdit && (
            <div className="kp-settings__readonly">
              Yalnizca tenant_admin rolu bu sayfayi duzenleyebilir; mevcut ayarlar salt-okunur olarak gosteriliyor.
            </div>
          )}
          {loading && <div className="kp-settings__placeholder">{UI_LOADING}</div>}
          {error && <div className="kp-settings__error">{error}</div>}

          <div className="kp-settings__grid">
            <label className="kp-settings__field">
              <span>Kurum Adi (Gorunecek)</span>
              <input
                type="text"
                value={form.displayName}
                maxLength={150}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </label>
            <label className="kp-settings__field">
              <span>Yasal Unvan</span>
              <input
                type="text"
                value={form.legalName}
                maxLength={200}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              />
            </label>
            <label className="kp-settings__field">
              <span>Telefon</span>
              <input
                type="text"
                value={form.phone}
                maxLength={30}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label className="kp-settings__field">
              <span>E-posta</span>
              <input
                type="email"
                value={form.email}
                maxLength={150}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className="kp-settings__field">
              <span>Web Sitesi</span>
              <input
                type="url"
                placeholder="https://"
                value={form.website}
                maxLength={200}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </label>
            <label className="kp-settings__field">
              <span>Vergi / MERSIS No</span>
              <input
                type="text"
                value={form.taxNumber}
                maxLength={50}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, taxNumber: e.target.value })}
              />
            </label>
            <label className="kp-settings__field kp-settings__field--full">
              <span>Adres</span>
              <textarea
                rows={2}
                maxLength={1000}
                value={form.address}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </label>
            <label className="kp-settings__field">
              <span>Zaman Dilimi</span>
              <select
                value={form.timezone}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
                {!TIMEZONE_OPTIONS.includes(form.timezone) && (
                  <option value={form.timezone}>{form.timezone}</option>
                )}
              </select>
            </label>
            <label className="kp-settings__field">
              <span>
                Varsayilan Ders Suresi (dk) <em>*</em>
              </span>
              <input
                type="number"
                min={15}
                max={600}
                step={5}
                value={form.defaultLessonDurationMinutes}
                disabled={!canEdit || submitting}
                onChange={(e) =>
                  setForm({
                    ...form,
                    defaultLessonDurationMinutes: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
            <div className="kp-settings__field kp-settings__field--full">
              <span>
                Çalışma Gunleri <em>*</em>
              </span>
              <div className="kp-settings__days">
                {ALL_WORKING_DAYS.map((day) => {
                  const active = form.workingDays.includes(day);
                  return (
                    <button
                      type="button"
                      key={day}
                      className={`kp-settings__day-chip${active ? " kp-settings__day-chip--active" : ""}`}
                      onClick={() => canEdit && !submitting && toggleDay(day)}
                      disabled={!canEdit || submitting}
                    >
                      {WORKING_DAY_LABELS[day]}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="kp-settings__field">
              <span>Marka Birincil Renk</span>
              <div className="kp-settings__color-row">
                <input
                  type="color"
                  value={form.brandPrimaryColor || "#2563eb"}
                  disabled={!canEdit || submitting}
                  onChange={(e) =>
                    setForm({ ...form, brandPrimaryColor: e.target.value.toUpperCase() })
                  }
                />
                <input
                  type="text"
                  placeholder="#RRGGBB"
                  value={form.brandPrimaryColor}
                  maxLength={9}
                  disabled={!canEdit || submitting}
                  onChange={(e) => setForm({ ...form, brandPrimaryColor: e.target.value })}
                />
              </div>
            </label>
            <label className="kp-settings__field">
              <span>Marka Ikincil Renk</span>
              <div className="kp-settings__color-row">
                <input
                  type="color"
                  value={form.brandSecondaryColor || "#10b981"}
                  disabled={!canEdit || submitting}
                  onChange={(e) =>
                    setForm({ ...form, brandSecondaryColor: e.target.value.toUpperCase() })
                  }
                />
                <input
                  type="text"
                  placeholder="#RRGGBB"
                  value={form.brandSecondaryColor}
                  maxLength={9}
                  disabled={!canEdit || submitting}
                  onChange={(e) => setForm({ ...form, brandSecondaryColor: e.target.value })}
                />
              </div>
            </label>
            <label className="kp-settings__field kp-settings__field--full">
              <span>Logo URL</span>
              <input
                type="url"
                placeholder="https://cdn.../logo.png"
                value={form.logoUrl}
                maxLength={200}
                disabled={!canEdit || submitting}
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              />
            </label>
          </div>

          {saveError && <div className="kp-settings__error" style={{ marginTop: 12 }}>{saveError}</div>}
          {saveOk && <div className="kp-settings__success" style={{ marginTop: 12 }}>Ayarlar kaydedildi.</div>}

          {canEdit && (
            <div className="kp-settings__actions">
              <button
                type="button"
                className="kp-btn"
                disabled={submitting || !dirty}
                onClick={() => setForm(settingsToForm(data))}
              >
                Sıfırla
              </button>
              <button
                type="submit"
                className="kp-btn kp-btn--primary"
                disabled={submitting || !dirty}
              >
                {submitting ? "Kaydediliyor…" : "Kaydet"}
              </button>
            </div>
          )}
        </form>
      )}

      {tab === "schedule" && (
        <div className="kp-card kp-settings__panel">
          <ScheduleSettingsSummary />
        </div>
      )}
    </div>
  );
}
